import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { User, Lock, Briefcase, Save } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminProfile() {
  const { t } = useAdminLanguage();
  const { user, refreshUser } = useOutletContext();
  const [form, setForm] = useState({ name: "", position: "", photo_url: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        position: user.position || "",
        photo_url: user.photo_url || ""
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(t("save") + " OK");
        refreshUser();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("error_generic"));
      }
    } catch {
      toast.error(t("connection_error"));
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t("passwords_mismatch"));
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error(t("password_min_length"));
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });
      if (res.ok) {
        toast.success(t("password_configured"));
        setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      } else {
        const err = await res.json();
        toast.error(err.detail || t("error_generic"));
      }
    } catch {
      toast.error(t("connection_error"));
    }
    setSaving(false);
  };

  const getInitials = (name) => {
    if (!name) return "A";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="admin-profile-page">
      <h1 className="text-2xl font-display font-bold text-white">Profil</h1>

      {/* Profile Info */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <User className="w-5 h-5" /> {t("name")} & {t("role")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <Avatar className="h-16 w-16 border-2 border-indigo-500">
              <AvatarImage src={form.photo_url} />
              <AvatarFallback className="bg-indigo-600 text-white text-lg">{getInitials(form.name)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white font-medium">{user.email}</p>
              <p className="text-xs text-zinc-500">{user.role === "super_admin" ? "Super Admin" : t("admin_employee")}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">{t("name")}</Label>
            <Input
              data-testid="profile-name-input"
              className="bg-zinc-950 border-zinc-800 text-white"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2"><Briefcase className="w-3 h-3" /> Position</Label>
            <Input
              data-testid="profile-position-input"
              className="bg-zinc-950 border-zinc-800 text-white"
              placeholder="Ex: Directeur RH"
              value={form.position}
              onChange={e => setForm({ ...form, position: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Photo URL</Label>
            <Input
              data-testid="profile-photo-input"
              className="bg-zinc-950 border-zinc-800 text-white"
              placeholder="https://..."
              value={form.photo_url}
              onChange={e => setForm({ ...form, photo_url: e.target.value })}
            />
          </div>

          <Button
            data-testid="save-profile-btn"
            onClick={handleSaveProfile}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Save className="w-4 h-4 mr-2" /> {t("save")}
          </Button>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5" /> {t("password")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">{t("password")} actuel</Label>
            <Input
              data-testid="current-password-input"
              type="password"
              className="bg-zinc-950 border-zinc-800 text-white"
              value={passwordForm.current_password}
              onChange={e => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">{t("password")} nouveau</Label>
            <Input
              data-testid="new-password-input"
              type="password"
              className="bg-zinc-950 border-zinc-800 text-white"
              value={passwordForm.new_password}
              onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">{t("confirm_password")}</Label>
            <Input
              data-testid="confirm-new-password-input"
              type="password"
              className="bg-zinc-950 border-zinc-800 text-white"
              value={passwordForm.confirm_password}
              onChange={e => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
            />
          </div>
          <Button
            data-testid="change-password-btn"
            onClick={handleChangePassword}
            disabled={saving}
            variant="outline"
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
          >
            <Lock className="w-4 h-4 mr-2" /> {t("save")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
